import {ArrowLeft, Loader2, Sigma} from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { authErrorMessage } from '@/lib/authErrors'
import { lumi } from '@/lib/lumi'

type Mode = 'signin' | 'signup' | 'reset'

function Auth() {
  const { isAuthenticated } = useAuth()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/'
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(countdown - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const switchMode = (next: Mode) => {
    setMode(next)
    setError('')
    setOtp('')
    setOtpSent(false)
    setPassword('')
  }

  const sendOtp = async () => {
    if (!email) {
      setError('Please enter your email first.')
      return
    }
    setError('')
    try {
      await lumi.auth.sendOtp({
        email,
        type: mode === 'reset' ? 'resetPassword' : 'register',
      })
      setOtpSent(true)
      setCountdown(60)
    }
    catch (err) {
      setError(authErrorMessage(err))
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email, password })
      }
      else if (mode === 'signup') {
        if (!otpSent) {
          setError('Send and enter the OTP code before creating your account.')
          setLoading(false)
          return
        }
        await lumi.auth.signUpWithPassword({ email, password, otp })
      }
      else {
        if (!otpSent) {
          setError('Send and enter the OTP code before resetting your password.')
          setLoading(false)
          return
        }
        await lumi.auth.resetPassword({ email, newPassword: password, otp })
      }
      window.location.href = '/'
    }
    catch (err) {
      setError(authErrorMessage(err))
    }
    setLoading(false)
  }

  const google = async () => {
    setError('')
    try {
      await lumi.auth.signInWithOAuth({ provider: 'google' })
    }
    catch (err) {
      setError(authErrorMessage(err))
    }
  }

  const title = mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create student account' : 'Reset password'
  const subtitle = mode === 'signin'
    ? 'Sign in to email step-by-step solutions to your inbox.'
    : mode === 'signup'
      ? 'Register once, then email any solver walkthrough to yourself.'
      : 'Use the OTP sent to your email to set a new password.'

  return (
    <div className="min-h-screen text-slate-100 flex flex-col">
      <header className="border-b border-[var(--line)] bg-[#0b0f19]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Sigma className="w-5 h-5 text-[#06281b]" strokeWidth={2.5} />
            </span>
            <span className="font-display text-[17px] font-semibold tracking-tight">
              FunMathCalculator<span className="text-emerald-400">.com</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200">
            <ArrowLeft className="w-4 h-4" /> Back to solvers
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <form onSubmit={submit} className="w-full max-w-md glass glow-border rounded-2xl p-6 md:p-8 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/80 font-medium">Student inbox</div>
            <h1 className="font-display text-3xl font-semibold mt-2">{title}</h1>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{subtitle}</p>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-wider text-slate-400">Email</span>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-[var(--line)] text-sm text-slate-100 focus:outline-none focus:border-emerald-400/50"
              placeholder="you@school.edu"
            />
          </label>

          {(mode === 'signup' || mode === 'reset') ? (
            <div className="space-y-1.5">
              <span className="text-xs uppercase tracking-wider text-slate-400">OTP code</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  required
                  className="flex-1 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-[var(--line)] text-sm text-slate-100 focus:outline-none focus:border-emerald-400/50"
                  placeholder="6-digit code"
                />
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={countdown > 0}
                  className="shrink-0 px-3.5 py-2.5 rounded-xl border border-emerald-400/40 text-emerald-200 text-sm font-medium hover:bg-emerald-400/10 disabled:opacity-50"
                >
                  {countdown > 0 ? `${countdown}s` : otpSent ? 'Resend OTP' : 'Send OTP'}
                </button>
              </div>
            </div>
          ) : null}

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-wider text-slate-400">
              {mode === 'reset' ? 'New password' : 'Password'}
            </span>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-[var(--line)] text-sm text-slate-100 focus:outline-none focus:border-emerald-400/50"
              placeholder={mode === 'reset' ? 'New password' : 'Password'}
            />
          </label>

          {error ? (
            <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-3.5 py-2.5 text-sm text-rose-200">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading || ((mode === 'signup' || mode === 'reset') && !otpSent)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-400 text-[#06281b] font-semibold hover:bg-emerald-300 transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Working...' : mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
          </button>

          {mode !== 'reset' ? (
            <button
              type="button"
              onClick={google}
              className="w-full px-4 py-3 rounded-xl border border-[var(--line-strong)] bg-white/[0.03] text-sm font-medium text-slate-200 hover:border-cyan-400/40"
            >
              Continue with Google
            </button>
          ) : null}

          <div className="text-sm text-slate-400 space-y-2">
            {mode === 'signin' ? (
              <>
                <button type="button" onClick={() => switchMode('reset')} className="text-cyan-300 hover:text-cyan-200">
                  Forgot password?
                </button>
                <div>
                  No account?{' '}
                  <button type="button" onClick={() => switchMode('signup')} className="text-emerald-300 hover:text-emerald-200">
                    Register
                  </button>
                </div>
              </>
            ) : null}
            {mode === 'signup' ? (
              <div>
                Already registered?{' '}
                <button type="button" onClick={() => switchMode('signin')} className="text-emerald-300 hover:text-emerald-200">
                  Sign in
                </button>
              </div>
            ) : null}
            {mode === 'reset' ? (
              <button type="button" onClick={() => switchMode('signin')} className="text-emerald-300 hover:text-emerald-200">
                Back to sign in
              </button>
            ) : null}
          </div>
        </form>
      </main>
    </div>
  )
}

export default Auth
